import { useEffect, useState, useRef } from 'react';
import { useGet } from '@/query/fetchHooks';
import { sortDataLP, categoriesIcon } from '@/components/LearningPath/util';
import { IconButton, IconTile, Button } from '@teo/components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Training,
  Theme,
  Evaluation,
  Flag,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
  Options,
  ChevronRight,
  ChevronDown,
} from '@teo/components/icons';
import { nthLastIndexOf, parseCourseType } from '@/util';
import { ModalSettingsTraining } from './ModalSettingsTraining';

const Card = ({
  item,
  organisationId,
  setIsOpenSettings,
  setSelectTrainig,
  allCategories,
  subContent = false,
}) => {
  const { mandatory, name, type, subType, picture, objectId, tags } = item;
  const [subTree, setSubTree] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const basePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 3)
  );

  const [isOpenTheme, setIsOpenTheme] = useState(false);
  const [height, setHeight] = useState(0);

  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpenTheme && contentRef.current) {
      setHeight(contentRef.current.scrollHeight + 12);
    } else {
      setHeight(0);
    }
  }, [isOpenTheme]);

  useGet(`/learningpathnodedetails`, {
    learningpathId: objectId,
    organisationId: organisationId,
    enabled: type === 'learningpath',
    onSuccess: async (res) => {
      const nodedetails = res?.data?.filter((el) => el?.type !== 'root');
      if (nodedetails?.length > 0) {
        const sortData = sortDataLP(nodedetails);
        setSubTree(sortData);
      }
    },
  });

  let Icon;

  if (tags && type === 'learningpath') {
    const learningpathTags = allCategories?.find(
      (el) => el?.label === tags.split(':')[1]
    );
    Icon = categoriesIcon(learningpathTags?.icon);
  } else {
    Icon = Theme;
  }

  return (
    <div
      className={`flex flex-col rounded-lg border p-2 ${
        !mandatory && 'border-dashed'
      } ${subContent && ' border-0 bg-grey-01'}`}
    >
      <div className="flex items-center">
        {type === 'learningpath' ? (
          <Button
            className="mr-2 !px-1"
            Icon={isOpenTheme ? ChevronDown : ChevronRight}
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsOpenTheme(!isOpenTheme);
            }}
          >
            {isOpenTheme ? (
              <ChevronDown className="w-4 text-grey-05" />
            ) : (
              <ChevronRight className="w-4 text-grey-05" />
            )}
          </Button>
        ) : (
          !subContent && <div className="w-8 "></div>
        )}
        <IconTile
          Icon={
            subType === 'training'
              ? Training
              : subType === 'learningpath'
              ? Icon
              : subType === 'milestone'
              ? Flag
              : subType === 'evaluation'
              ? Evaluation
              : subType === 'course'
              ? DuoToneCourseFilled
              : subType === 'assessment'
              ? DuoToneAssessmentFilled
              : subType === 'leervacature'
              ? DuoToneVacancyFilled
              : DuoToneCourseFilled
          }
          imageUrl={picture}
          color={
            subType === 'training'
              ? 'green'
              : subType === 'learningpath'
              ? 'dark_blue'
              : subType === 'milestone'
              ? 'blue'
              : subType === 'evaluation'
              ? 'purple'
              : subType === 'course'
              ? 'yellow'
              : subType === 'assessment'
              ? 'red'
              : subType === 'leervacature'
              ? 'yellow'
              : 'yellow'
          }
          size="small_md"
          className={
            subType === 'course'
              ? 'fill-primary-06 text-primary-01'
              : subType === 'assessment'
              ? 'fill-tint-red-dark text-tint-red-light'
              : subType === 'leervacature' && 'fill-error-04 text-error-04'
          }
        />
        <div className="flex flex-1 flex-col gap-1 px-3">
          <p className="text-xs text-grey-06">
            {subType === 'training'
              ? t('pages.learning_path.training')
              : subType === 'learningpath'
              ? t('pages.learning_path.theme')
              : subType === 'milestone'
              ? t('pages.learning_path.milestone')
              : subType === 'evaluation'
              ? t('pages.learning_path.evaluation')
              : subType === 'course'
              ? t('links.course')
              : subType === 'assessment'
              ? t('links.assessment')
              : subType === 'leervacature'
              ? t('links.vacancy')
              : t('links.course')}
          </p>
          <p
            className={`max-w-[225px] break-words text-left text-sm font-medium text-black line-clamp-2 `}
          >
            {name}
          </p>
        </div>
        {type !== 'learningpath' && (
          <IconButton
            className=""
            Icon={Options}
            variant="ghost"
            size="md"
            onClick={() => {
              if (type === 'course') {
                navigate(
                  `${basePath}/${parseCourseType(subType)}/details/${objectId}`
                );
              }
              if (type === 'training') {
                setSelectTrainig(item);
                setIsOpenSettings(true);
              }
            }}
          />
        )}
      </div>
      <div
        style={{
          overflow: 'hidden',
          maxHeight: height,
          transition: 'max-height 0.4s ease',
        }}
      >
        {subTree?.length > 0 && (
          <div className={`mt-3 flex flex-col gap-3`} ref={contentRef}>
            {subTree?.map((el) => {
              return (
                <Card
                  key={el?.id}
                  item={el}
                  organisationId={organisationId}
                  setIsOpenSettings={setIsOpenSettings}
                  setSelectTrainig={setSelectTrainig}
                  allCategories={allCategories}
                  subContent={true}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const LearningPathContent = ({
  authOrg,
  authUser,
  content,
  learningpath,
  organisationId,
  setResetContent,
}) => {
  const [tree, setTree] = useState(null);
  const [selectTrainig, setSelectTrainig] = useState(null);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [allCategories, setAllCategories] = useState([]);

  useGet(`/categories`, {
    sort: 'tag',
    organisationId: `${authOrg},OR,organisationId=null`,
    onSuccess: (result) => {
      setAllCategories(
        result?.data?.map((el) => ({
          id: el.id,
          label: el.tag,
          icon: el.icon,
        })) || []
      );
    },
  });

  useEffect(() => {
    if (content?.length > 0) {
      const sortData = sortDataLP(content);
      setTree(sortData);
    }
  }, [content]);

  return (
    <div className="flex flex-col gap-3">
      {tree?.map((el) => {
        return (
          <Card
            key={el?.id}
            item={el}
            organisationId={organisationId}
            setIsOpenSettings={setIsOpenSettings}
            setSelectTrainig={setSelectTrainig}
            allCategories={allCategories}
          />
        );
      })}
      {isOpenSettings && (
        <ModalSettingsTraining
          authOrg={authOrg}
          authUser={authUser}
          learningpath={learningpath}
          isOpenSettings={isOpenSettings}
          setIsOpenSettings={setIsOpenSettings}
          setSelectTrainig={setSelectTrainig}
          selectTrainig={selectTrainig}
          setResetContent={setResetContent}
        />
      )}
    </div>
  );
};
